import { z } from 'zod'
import type { Call } from './content'

/**
 * Numbering notes, and deciding where they are set.
 *
 * The number is COMPUTED here and stored nowhere. A marker and its note are one
 * fact; the moment a number is written down they are two, and two facts can
 * disagree — which is exactly what happens to a book numbered by hand, silently,
 * in a draft nobody re-reads from the front.
 */

/**
 * Foot of the page, or gathered at the back.
 *
 * A book may set both, and the two are the scholarly apparatus's two jobs: a
 * remark the reader wants WHILE reading belongs where their eye already is, and
 * a reference they will look up afterwards belongs where it does not interrupt
 * a page. So this is the note's own, not only the book's.
 */
export const notePlacementSchema = z.enum(['foot', 'end'])
export type NotePlacement = z.infer<typeof notePlacementSchema>

export const markerStyleSchema = z.enum(['arabic', 'roman', 'symbol'])
export type MarkerStyle = z.infer<typeof markerStyleSchema>

/**
 * Where notes are set, and how they are counted.
 *
 * A Zod schema rather than a bare type, because this is a book SETTING: it
 * arrives from a caller, is stored as JSON, and is read back by the renderer.
 * Two of those three are places an unvalidated shape reaches paper from.
 */
export const notePolicySchema = z.object({
  /**
   * Where a note goes when it does not say for itself.
   *
   * PLACEMENT is the one setting here that is print's alone. A footnote is
   * close and cramped; an endnote is roomy and far, and neither trade-off
   * applies on a screen, where a note opens where it stands. Everything else
   * decides the NUMBER, which the screen and the page must agree on or a reader
   * looking note 4 up in the book finds a different note in the app.
   */
  placement: notePlacementSchema,
  /** Restart the count at each scope, or run straight through the book. */
  grouping: z.enum(['continuous', 'per-section']),
  /** Which level restarts it. A chapter, usually. */
  scopeKind: z.enum(['division', 'part']),
  /**
   * One style per placement, and they must differ.
   *
   * The two streams are counted independently, so a book setting both to
   * figures prints two notes called 1 on one page and gives a reader no way to
   * tell which of them the marker meant. Symbols at the foot against figures at
   * the back is the convention, and it is the convention BECAUSE it answers
   * that: the shape of the mark says which apparatus to look in.
   */
  markerStyle: z
    .object({
      foot: markerStyleSchema,
      end: markerStyleSchema,
    })
    /* Refused rather than merely discouraged: the two being different is what
       the reader reads, so a book set both ways to figures is one whose marks
       carry no information at all. */
    .refine(
      (m) => m.foot !== m.end,
      'Footnotes and endnotes are counted apart, so they take different marks.',
    ),
})
export type NotePolicy = z.infer<typeof notePolicySchema>

export const DEFAULT_NOTE_POLICY: NotePolicy = {
  placement: 'end',
  grouping: 'per-section',
  scopeKind: 'division',
  markerStyle: { foot: 'symbol', end: 'arabic' },
}

/** Enough of a book's shape to number its notes. */
export type NumberingInput = {
  sections: {
    id: string
    kind: string
    /**
     * What the part's own HEADING calls, in reading order.
     *
     * A title is text and may mark a note. Numbering walked passages only, so
     * such a note was counted nowhere, printed nowhere and listed nowhere while
     * its marker sat on the page.
     */
    calls?: Call[]
    blocks: { id: string; calls: Call[] }[]
    children: NumberingInput['sections']
  }[]
  policy?: NotePolicy
  /**
   * Where each note is set, for the notes that say for themselves.
   *
   * Numbering has to know: the two streams count separately, so which stream a
   * note is in decides its number and not merely where its body is printed.
   */
  placements?: Map<string, NotePlacement>
}

export type NumberedNote = {
  noteId: string
  /** What the marker prints. A string because it is not always a number. */
  marker: string
  /** Which apparatus it is set in, and therefore which stream counted it. */
  placement: NotePlacement
  /** The scope it is counted within — the group an endnote list is headed by. */
  scope: string
  scopeTitleId: string
}

const ROMAN: [number, string][] = [
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
]

const toRoman = (n: number): string => {
  let out = ''
  let left = n
  for (const [value, sign] of ROMAN) {
    while (left >= value) {
      out += sign
      left -= value
    }
  }
  return out
}

/** `*`, `†`, `‡` and on — the traditional sequence, for a book that wants it. */
const SYMBOLS = ['*', '†', '‡', '§', '‖', '¶']
const toSymbol = (n: number): string => {
  // Doubled once the sequence runs out, which is what a typesetter does.
  const round = Math.floor((n - 1) / SYMBOLS.length) + 1
  return (SYMBOLS[(n - 1) % SYMBOLS.length] as string).repeat(round)
}

const marker = (n: number, style: MarkerStyle): string =>
  style === 'roman' ? toRoman(n) : style === 'symbol' ? toSymbol(n) : String(n)

/**
 * Every note in the book, numbered in reading order.
 *
 * Reading order is the whole definition: a note is numbered by where its MARKER
 * is, not by where its body sits, which is why moving a paragraph renumbers
 * everything after it without anybody editing a number.
 *
 * **The two placements are counted separately**, each in its own style, so the
 * `*` at the foot and the `1` at the back are two apparatuses a reader can tell
 * apart rather than two notes both called 1. A book using one placement is that
 * same rule with an empty stream beside it.
 *
 * A note referenced twice keeps the number of its first reference. The second
 * marker points at the same note, and giving it a new number would claim there
 * are two.
 */
export function numberNotes(input: NumberingInput): NumberedNote[] {
  const policy = input.policy ?? DEFAULT_NOTE_POLICY
  const out: NumberedNote[] = []
  const seen = new Map<string, NumberedNote>()

  let scope = ''
  let scopeTitleId = ''
  const count: Record<NotePlacement, number> = { foot: 0, end: 0 }

  const walk = (sections: NumberingInput['sections']) => {
    for (const s of sections) {
      const restarts =
        policy.grouping === 'per-section' && s.kind === policy.scopeKind
      if (restarts) {
        scope = s.id
        scopeTitleId = s.id
        count.foot = 0
        count.end = 0
      }
      /* The heading first, because that is where a reader meets it: a note
         marked on a part's title is called before anything under that part. */
      const marked = [
        ...(s.calls ?? []),
        ...s.blocks.flatMap((b) => b.calls),
      ].filter((c) => c.of === 'note')
      for (const { id: noteId } of marked) {
        const already = seen.get(noteId)
        if (already !== undefined) {
          /*
           * A second call repeats the whole entry, SCOPE included — a note is
           * printed once, in the chapter that counted it. Taking the current
           * scope instead would list the body again under the later chapter,
           * carrying a number that chapter counted to something else: two
           * items marked 1 in one list, and an anchor id written twice.
           */
          out.push(already)
          continue
        }
        // A note that says nothing takes the book's placement, which is what
        // makes "set this book at the foot" one edit of one column.
        const placement = input.placements?.get(noteId) ?? policy.placement
        count[placement] += 1
        const entry: NumberedNote = {
          noteId,
          marker: marker(count[placement], policy.markerStyle[placement]),
          placement,
          scope,
          scopeTitleId,
        }
        seen.set(noteId, entry)
        out.push(entry)
      }
      walk(s.children)
    }
  }
  walk(input.sections)
  return out
}

/** Only the notes set in one apparatus — the two are counted and printed apart. */
export const notesPlaced = (
  numbered: NumberedNote[],
  placement: NotePlacement,
): NumberedNote[] => numbered.filter((n) => n.placement === placement)

/**
 * The placements notes state for themselves, ready for `numberNotes`.
 *
 * A note saying nothing is left out rather than resolved here: what it means is
 * "the book's", and the policy is what knows that.
 */
export const placementsOf = (
  notes: { id: string; placement: NotePlacement | null }[],
): Map<string, NotePlacement> =>
  new Map(
    notes
      .filter(
        (n): n is { id: string; placement: NotePlacement } => !!n.placement,
      )
      .map((n) => [n.id, n.placement]),
  )

/** The marker for each note, for a renderer that has one in its hand. */
export function markersByNote(numbered: NumberedNote[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const n of numbered) if (!map.has(n.noteId)) map.set(n.noteId, n.marker)
  return map
}

/**
 * Notes gathered by the scope they are counted in — an endnote section's
 * headings and their lists, in reading order.
 */
export function notesByScope(
  numbered: NumberedNote[],
): { scope: string; notes: NumberedNote[] }[] {
  const order: string[] = []
  const groups = new Map<string, NumberedNote[]>()
  for (const n of numbered) {
    if (!groups.has(n.scope)) {
      groups.set(n.scope, [])
      order.push(n.scope)
    }
    // A second reference to the same note does not get a second entry in the
    // list at the back: there is one note, and it is printed once.
    const list = groups.get(n.scope) as NumberedNote[]
    if (!list.some((x) => x.noteId === n.noteId)) list.push(n)
  }
  return order.map((scope) => ({
    scope,
    notes: groups.get(scope) as NumberedNote[],
  }))
}
